/****************************************************************************
 * Copyright 2021 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/

import { error } from './constants';
import { useEffect, useRef } from 'react';

import styles from './InfoModal.module.less';

interface InfoModalProps {
  message: string;
  close: () => void;
}

const InfoModal = ({ message, close }: InfoModalProps): JSX.Element => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }

    return () => {
      if (dialog?.open) {
        dialog.close();
      }
    };
  }, []);

  return (
    <div className={styles.infoModalOverlay}>
      <dialog
        ref={dialogRef}
        className={styles.infoModalWindow}
        aria-labelledby="dialog-label"
        aria-describedby="dialog-content"
        data-testid="info-modal-window"
      >
        <header id="dialog-label">{error.message}</header>
        <div
          className={styles.infoModalBody}
          id="dialog-content"
          data-testid="error-message-body"
        >
          {message}
        </div>
        <footer>
          <button
            className={styles.ok}
            onClick={close}
            data-testid="info-modal-close"
          >
            {error.close}
          </button>
        </footer>
      </dialog>
    </div>
  );
};

export default InfoModal;
